import { Link } from "@tanstack/react-router";
import { Logo } from "@/components/ui/logo";
import { buttonVariants } from "@/components/ui/button-util";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: Privacy,
});

function Privacy() {
  return (
    <div className="relative min-h-screen w-full bg-card">
      {/* Navigation */}
      <div className="sticky top-0 z-50 border-b border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/75">
        <div className="mx-auto flex w-full max-w-screen-lg items-center justify-between p-4">
          <Link to="/" className="flex h-10 items-center gap-1">
            <Logo />
          </Link>
          <Link
            to="/"
            className={buttonVariants({
              variant: "ghost",
              size: "sm",
            })}
          >
            Back to Home
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="prose prose-lg dark:prose-invert mx-auto">
          <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>

          <div className="mt-8 space-y-12">
            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Introduction
              </h2>
              <p className="mt-4 text-muted-foreground">
                CreatorGrid is committed to protecting your privacy. This
                Privacy Policy outlines the types of personal information we
                collect and process, how we use it, and the steps we take to
                ensure your personal information is handled appropriately while
                using our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                User Registration
              </h2>
              <p className="mt-4 text-muted-foreground">
                When you register for CreatorGrid, we collect your primary
                account information, which includes user ID, name, profile
                image, and date joined, to enable the creation of your user
                profile and to provide you with a personalized experience when
                the app is being signed in for the first time, in the
                foreground. Registration is facilitated through Apple Sign In
                for iOS devices. These information are encrypted in transit and
                in Cloud Storage. Lastly, CreatorGrid collects the device token
                to enable push notifications using Segment. This is a key step
                to help us send you important messages, such as maintenance
                events, as well as personalised marketing promotions. You can
                unsubscribe from those at any moment.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Data Use and Sharing
              </h2>
              <p className="mt-4 text-muted-foreground">
                The data collected is used to provide and improve our services
                to you. We do not share your personal information with third
                parties, except as required by law or as necessary for the
                services provided by third-party processors, which are bound by
                privacy obligations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                User Data Management
              </h2>
              <p className="mt-4 text-muted-foreground">
                You have the right to access, edit, or delete your account at
                any time. The process for deleting your account is provided
                within the app, and upon deletion, all associated data will be
                permanently removed from our records.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Data Retention and Deletion
              </h2>
              <p className="mt-4 text-muted-foreground">
                We retain your personal information only for as long as
                necessary to provide you with our services. We also retain and
                use your information as necessary to comply with our legal
                obligations, resolve disputes, and enforce our agreements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Security Measures
              </h2>
              <p className="mt-4 text-muted-foreground">
                We take the security of your data seriously and implement a
                variety of security measures to maintain the safety of your
                personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Changes to the Privacy Policy
              </h2>
              <p className="mt-4 text-muted-foreground">
                We may update this Privacy Policy from time to time. When we do,
                we will notify you either by posting the new policy on our
                website or through other communications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Contact Information
              </h2>
              <p className="mt-4 text-muted-foreground">
                If you have any questions about this Privacy Policy, please
                contact us at{" "}
                <a
                  href="mailto:creators@creatorgrid.io"
                  className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  creators@creatorgrid.io
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                Compliance and Cooperation with Regulatory Authorities
              </h2>
              <p className="mt-4 text-muted-foreground">
                We regularly review our compliance with this Privacy Policy and
                adhere to various regulatory standards. If we receive formal
                written complaints, we will follow up with the person who made
                the complaint to address their concerns.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
